import React, {useCallback, useEffect, useState, useMemo} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Layout} from "../../pages/siren/Layout";
import {BlueButton} from "../../components/ui/Buttons";
import {SirenDefaultDeliveryMethods} from "./hooks/useGetSirenDefaultDeliveryMethods";
import {useUpsertSirenDefaultDeliveryMethods} from "./hooks/useUpsertSirenDefaultDeliveryMethods";
import {useDeleteSirenDefaultDeliveryMethods} from "./hooks/useDeleteSirenDefaultDeliveryMethods";
import {platformAPI} from "../../api/axiosConfig";
import {Client, Department} from "../../types/userManagement";
import {DeliveryMethodItem} from "../../pages/siren/subpages/TemplateForm";
import BasicSelect from "../../components/form-v2/basic-select";
import {ButtonV2} from "../../components/ui/ButtonsV2/ButtonV2";
import {closestCenter, DndContext, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {arrayMove, SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import {filterMethodsByDriverTypes, useAvailableDriverTypes} from "../../pages/siren/hooks/useAvailableDriverTypes";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import {ScopeSplitSelector, ScopeSelection} from "./components/ScopeSplitSelector";
import {IconButton} from "./import/inputs";
import {useScopeQueryParams} from "./hooks/useScopeQueryParams";
import {useSiren} from "../../pages/siren/SirenContext";
import {ClientSelectSingle} from "../../components/form-v2/searchable-select/ClientSelect";
import {DepartmentSelectSingle} from "../../components/form-v2/searchable-select/DepartmentSelect";


function formatMethod(method: string): string {
    return method
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\bSms\b/g, "SMS");
}

function DeliveryMethodPills({methods}: {methods: string[]}) {
    const [expanded, setExpanded] = useState(false);
    if (methods.length === 0) return <span className="text-grey text-sm">—</span>;
    const limit = 3;
    const visible = expanded ? methods : methods.slice(0, limit);
    const hidden = methods.length - limit;
    return (
        <div className="flex flex-wrap gap-1 items-center" onClick={e => e.stopPropagation()}>
            {visible.map(m => (
                <span key={m} className="px-2 py-0.5 rounded-full bg-light-light-grey text-xs text-dark-dark-grey whitespace-nowrap">
                    {formatMethod(m)}
                </span>
            ))}
            {!expanded && hidden > 0 && (
                <button
                    className="px-2 py-0.5 rounded-full border border-light-grey text-xs text-grey hover:bg-light-light-grey transition-colors whitespace-nowrap"
                    onClick={() => setExpanded(true)}
                >
                    +{hidden}
                </button>
            )}
            {expanded && hidden > 0 && (
                <button
                    className="px-2 py-0.5 rounded-full border border-light-grey text-xs text-grey hover:bg-light-light-grey transition-colors"
                    onClick={() => setExpanded(false)}
                >
                    less
                </button>
            )}
        </div>
    );
}

const ALL_DELIVERY_METHOD_OPTIONS = [
    {value: "sms_work_mobile", label: "SMS Work Mobile"},
    {value: "sms_personal_mobile", label: "SMS Personal Mobile"},
    {value: "email_work", label: "Work Email"},
    {value: "email_personal", label: "Personal Email"},
    {value: "call_work_mobile", label: "Call Work Mobile"},
    {value: "call_personal_mobile", label: "Call Personal Mobile"},
    {value: "call_home", label: "Call Home"},
    {value: "app_aurora", label: "Aurora App"},
];

type FormState = {
    clientID: string;
    departmentID: string;
    deliveryMethods: string[];
};

const emptyForm = (): FormState => ({
    clientID: "",
    departmentID: "",
    deliveryMethods: [],
});

// --- Inline delivery methods panel ---

type DeliveryMethodsPanelProps = {
    scope: ScopeSelection;
    onDeleted: () => void;
};

const DeliveryMethodsPanel: React.FC<DeliveryMethodsPanelProps> = ({scope, onDeleted}) => {
    const {save, saving, error: saveError} = useUpsertSirenDefaultDeliveryMethods();
    const {deleteDeliveryMethods, deleting} = useDeleteSirenDefaultDeliveryMethods();
    const {hasFullAccess} = usePermissions();
    const availableDriverTypes = useAvailableDriverTypes(scope.client?.id ?? null);

    const [record, setRecord] = useState<SirenDefaultDeliveryMethods | null>(null);
    const [loading, setLoading] = useState(true);
    const [methods, setMethods] = useState<string[]>([]);
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState("");
    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        setLoading(true);
        setSaved(false);
        setRecord(null);
        const params: Record<string, string> = {};
        if (scope.client) params.clientID = scope.client.id;
        if (scope.department) params.departmentID = scope.department.id;
        platformAPI.get<SirenDefaultDeliveryMethods>("api/siren/default-delivery-methods/exact", {params})
            .then(res => {
                setRecord(res.data);
                setMethods(res.data.deliveryMethods ?? []);
            })
            .catch(() => setMethods([]))
            .finally(() => setLoading(false));
    }, [scope.client?.id, scope.department?.id]);

    const availableOptions = useMemo(() =>
        filterMethodsByDriverTypes(ALL_DELIVERY_METHOD_OPTIONS, availableDriverTypes)
            .filter(o => !methods.includes(o.value)),
        [methods, availableDriverTypes]
    );

    const handleAddMethod = useCallback(() => {
        if (!selectedMethod) return;
        setMethods(m => [...m, selectedMethod]);
        setSelectedMethod("");
    }, [selectedMethod]);

    const handleRemoveMethod = useCallback((method: string) => {
        setMethods(m => m.filter(x => x !== method));
    }, []);

    const handleDragEnd = useCallback((event: any) => {
        const {active, over} = event;
        if (active.id !== over?.id) {
            setMethods(m => {
                const oldIndex = m.indexOf(active.id);
                const newIndex = m.indexOf(over.id);
                return arrayMove(m, oldIndex, newIndex);
            });
        }
    }, []);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                deliveryMethods: methods,
            });
            setRecord(result);
            setSaved(true);
        } catch {
            // error shown via saveError
        }
    }, [scope, methods, save]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteDeliveryMethods(record.id, {clientID: scope.client?.id, departmentID: scope.department?.id});
            setRecord(null);
            setMethods([]);
            onDeleted();
        } catch {
            // silently ignore
        }
    }, [record, deleteDeliveryMethods, onDeleted]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-4 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div>
                <p className="font-semibold text-sm">{scope.client ? scope.client.name : "System Default"}</p>
                <p className="text-xs text-light-grey">{scope.client ? (scope.department ? scope.department.name : "Client level") : "Global fallback — no client"}</p>
            </div>

            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={methods} strategy={verticalListSortingStrategy}>
                    {methods.map((method, i) => (
                        <DeliveryMethodItem
                            key={method}
                            deliveryMethod={method as any}
                            index={i}
                            remove={() => handleRemoveMethod(method)}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {hasFullAccess("siren_config") && (
                <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-3">
                        <BasicSelect
                            disabled={availableOptions.length === 0}
                            options={availableOptions}
                            value={selectedMethod}
                            onChange={v => setSelectedMethod(v ?? "")}
                            useEmptyOption={true}
                        />
                    </div>
                    <ButtonV2
                        label={<AddIcon/>}
                        onClick={handleAddMethod}
                        disabled={availableOptions.length === 0}
                    />
                </div>
            )}
            </div>

            {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
            {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>}
            {hasFullAccess("siren_config") && (
                <div className="flex justify-end gap-2 items-center pb-2">
                    {record && (
                        <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-32" onClick={() => setDeleteConfirm(true)}/>
                    )}
                    <IconButton text={record ? "Update" : "Create"} icon={record ? <SaveIcon style={{fontSize: 16}}/> : <AddIcon style={{fontSize: 16}}/>} className="w-32" onClick={handleSave} loading={saving} disabled={saving}/>
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Default Delivery Methods</h3>
                        <p className="text-sm">Delete the delivery methods for <span className="font-semibold">{scope.client ? scope.client.name : "System Default"}</span>{scope.department ? <> / <span className="font-semibold">{scope.department.name}</span></> : ""}?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- List page ---

const SirenDefaultDeliveryMethodsPage: React.FC = () => {
    const {clientID} = useSiren();
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const {scope: selectedScope, drillClientID, drillClientName, deptPage, onSelect, onDrill, onDeptPageChange, onClear} = useScopeQueryParams({defaultClientID: isSuperAdmin ? null : clientID});

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ScopeSplitSelector
                        selectedScope={selectedScope}
                        onSelect={onSelect}
                        onClear={onClear}
                        onDrill={onDrill}
                        initialClientID={drillClientID}
                        initialClientName={drillClientName}
                        deptPage={deptPage}
                        onDeptPageChange={onDeptPageChange}
                        hydrate="deliveryMethodsExistence"
                        showSystemDefault={isSuperAdmin}
                        showClientLevel={!isDepartmentAdmin}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {selectedScope ? (
                        <DeliveryMethodsPanel
                            key={`${selectedScope.client?.id ?? "system"}-${selectedScope.department?.id ?? "client"}`}
                            scope={selectedScope}
                            onDeleted={() => {}}
                        />
                    ) : (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client, then a scope to configure.
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

// --- Edit / Create page ---

export const SirenDefaultDeliveryMethodsEditPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {save, saving, error: saveError} = useUpsertSirenDefaultDeliveryMethods();
    const {isSuperAdmin} = useAuth();
    const {hasFullAccess} = usePermissions();

    const [record, setRecord] = useState<SirenDefaultDeliveryMethods | null>(null);
    const [loading, setLoading] = useState(!!id);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saved, setSaved] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState("");

    const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | undefined>(undefined);

    const availableDriverTypes = useAvailableDriverTypes(form.clientID || null);
    const sensors = useSensors(useSensor(PointerSensor));

    React.useEffect(() => {
        if (!id) return;
        platformAPI.get<SirenDefaultDeliveryMethods>(`api/siren/default-delivery-methods/${id}`)
            .then(res => {
                setRecord(res.data);
                setForm({
                    clientID: res.data.clientID ?? "",
                    departmentID: res.data.departmentID ?? "",
                    deliveryMethods: res.data.deliveryMethods ?? [],
                });
            })
            .finally(() => setLoading(false));
    }, [id]);

    const availableOptions = useMemo(() =>
        filterMethodsByDriverTypes(ALL_DELIVERY_METHOD_OPTIONS, availableDriverTypes)
            .filter(o => !form.deliveryMethods.includes(o.value)),
        [form.deliveryMethods, availableDriverTypes]
    );

    const handleAddMethod = useCallback(() => {
        if (!selectedMethod) return;
        setForm(f => ({...f, deliveryMethods: [...f.deliveryMethods, selectedMethod]}));
        setSelectedMethod("");
    }, [selectedMethod]);

    const handleRemoveMethod = useCallback((method: string) => {
        setForm(f => ({...f, deliveryMethods: f.deliveryMethods.filter(m => m !== method)}));
    }, []);

    const handleDragEnd = useCallback((event: any) => {
        const {active, over} = event;
        if (active.id !== over?.id) {
            setForm(f => {
                const oldIndex = f.deliveryMethods.indexOf(active.id);
                const newIndex = f.deliveryMethods.indexOf(over.id);
                return {...f, deliveryMethods: arrayMove(f.deliveryMethods, oldIndex, newIndex)};
            });
        }
    }, []);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: form.clientID || undefined,
                departmentID: form.departmentID || undefined,
                deliveryMethods: form.deliveryMethods,
            });
            setRecord(result);
            setSaved(true);
            if (!id) navigate(`/siren/default-delivery-methods/edit/${result.id}`, {replace: true});
        } catch {
            // error shown via saveError
        }
    }, [form, save, id, navigate]);

    if (loading) return <Layout menuCurrent="config"><div className="p-4 text-sm text-light-grey">Loading…</div></Layout>;

    return (
        <Layout menuCurrent="config">
            <div className={`flex flex-col gap-4${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
                <div className="bg-white shadow-md rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">Scope</p>

                    {isSuperAdmin && (
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="font-medium text-sm">Client <span className="text-light-grey font-normal">(leave blank for system default)</span></label>
                                <ClientSelectSingle
                                    module="siren"
                                    client={selectedClient}
                                    initialClientID={form.clientID || undefined}
                                    onChange={c => {
                                        setSelectedClient(c);
                                        setSelectedDepartment(undefined);
                                        setForm(f => ({...f, clientID: c?.id ?? "", departmentID: ""}));
                                    }}
                                    disabled={!!record}
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="font-medium text-sm">Department <span className="text-light-grey font-normal">(optional)</span></label>
                                <DepartmentSelectSingle
                                    department={selectedDepartment}
                                    clientID={form.clientID || undefined}
                                    initialDepartmentID={form.departmentID || undefined}
                                    onChange={d => {
                                        setSelectedDepartment(d);
                                        setForm(f => ({...f, departmentID: d?.id ?? ""}));
                                    }}
                                    disabled={!!record || !form.clientID}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="bg-white shadow-md rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">Delivery Methods</p>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={form.deliveryMethods} strategy={verticalListSortingStrategy}>
                            {form.deliveryMethods.map((method, i) => (
                                <DeliveryMethodItem
                                    key={method}
                                    deliveryMethod={method as any}
                                    index={i}
                                    remove={() => handleRemoveMethod(method)}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>

                    {hasFullAccess("siren_config") && (
                        <div className="grid grid-cols-4 gap-4">
                            <div className="col-span-3">
                                <BasicSelect
                                    disabled={availableOptions.length === 0}
                                    options={availableOptions}
                                    value={selectedMethod}
                                    onChange={v => setSelectedMethod(v ?? "")}
                                    useEmptyOption={true}
                                />
                            </div>
                            <ButtonV2
                                label={<AddIcon/>}
                                onClick={handleAddMethod}
                                disabled={availableOptions.length === 0}
                            />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
                    {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Default delivery methods saved</div>}
                    {hasFullAccess("siren_config") && (
                        <div className="flex justify-end">
                            <div className="w-48">
                                <BlueButton text="Save" onClick={handleSave} loading={saving} disabled={saving}/>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SirenDefaultDeliveryMethodsPage;
